import { Box, Card, CardContent, Typography } from "@mui/material";
import type { ReactNode } from "react";

interface StatCardProps {
  icon: ReactNode;
  label: string;
  value: string;
  /** Hex color (6 chars); background uses `${accentColor}15`. */
  accentColor: string;
}

function splitValueAndCurrency(value: string): {
  amount: string;
  currency?: string;
} {
  const match = value.match(/^(.+?)\s+([A-Z]{3})$/);
  if (match) {
    return { amount: match[1], currency: match[2] };
  }
  return { amount: value };
}

export function StatCard({ icon, label, value, accentColor }: StatCardProps) {
  const { amount, currency } = splitValueAndCurrency(value);

  return (
    <Card elevation={0} sx={{ height: "100%", width: "100%" }}>
      <CardContent
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          p: 3,
          height: "100%",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: `${accentColor}15`,
            color: accentColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              minHeight: "2.5em",
              lineHeight: 1.25,
            }}
          >
            {label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.5,
              flexWrap: "nowrap",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              variant="h5"
              component="span"
              sx={{
                fontWeight: "bold",
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.125rem" },
                lineHeight: 1.2,
              }}
            >
              {amount}
            </Typography>
            {currency ? (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {currency}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
